// Shared realtime subscription helper for all /tickets/* pages.
//
// Subscribes to up to three tables that drive ticket triage / feed / board:
//   - public.tickets        (state, assignee_email, priority, labels changes)
//   - public.agent_presence (live-heartbeat updates → "Now" vs "Ongoing")
//   - public.agent_activity_log (picked_up / shipped events)
//
// Two-tier update strategy:
//   1. Optimistic in-memory patch (immediate, <1ms): when an UPDATE event
//      arrives with a full `new` payload, the onOptimisticPatch callback
//      applies it directly to root state — UI reacts within websocket RTT.
//   2. Debounced full refetch (150ms): reconciles derived state, parent/
//      child relationships, bucket re-classification.
//
// Reconnect behavior: previously hand-rolled exponential backoff on
// CLOSED / TIMED_OUT / CHANNEL_ERROR. Reconnect logic now lives inside
// the SDK provider's underlying transport (Supabase realtime auto-
// reconnects); this helper only handles the application-level subscribe/
// unsubscribe lifecycle.
//
// Usage:
//
//   // In a page's onCreate:
//   el._rtUnsub = subscribeTicketsRealtime({
//     pageName: 'general',
//     tables: ['tickets', 'agent_presence', 'agent_activity_log'],
//     onRefresh: async () => { ... },
//     onOptimisticPatch: (table, eventType, row) => { ... }
//   })
//
//   // In onRemove:
//   if (el._rtUnsub) { el._rtUnsub(); el._rtUnsub = null }
//
// migrated 2026-04-27 to SDK realtime — `workspaceProject.realtime.tickets`
// dispatches through the SDK's transport-agnostic provider with a `tables`
// selector. Today's wire is Supabase realtime via supabaseRealtimeProvider
// in bootShell; backend can swap to native socket.io without touching this
// file.

import { getSDK } from '../../../shared/functions/sdk.js'

const DEFAULT_TABLES = ['tickets', 'agent_presence', 'agent_activity_log']
// 150ms debounce keeps "Now" bucket current during rapid TEMO picks
// (PATCH ticket → PATCH presence within ~200ms; both batched into one
// refetch).
const DEBOUNCE_MS = 150

export const subscribeTicketsRealtime = ({
  pageName = 'tickets',
  tables = DEFAULT_TABLES,
  onRefresh,
  onOptimisticPatch
} = {}) => {
  if (typeof window === 'undefined') return () => {}

  let _resolved = null
  let _cancelled = false
  let _debounceTimer = null

  const _debounceRefresh = () => {
    if (!onRefresh) return
    if (_debounceTimer) clearTimeout(_debounceTimer)
    _debounceTimer = setTimeout(async () => {
      try { await onRefresh() }
      catch (err) { console.error(`[/tickets/${pageName}] refresh error`, err) }
    }, DEBOUNCE_MS)
  }

  const _onEvent = (event, payload) => {
    // Provider tags each event with payload._kind for table routing.
    const kind = payload?._kind || 'tickets'
    const tableName =
      kind === 'agent_presence' ? 'agent_presence'
      : kind === 'agent_activity_log' ? 'agent_activity_log'
      : 'tickets'
    const newRow = payload?.new
    if (onOptimisticPatch && newRow && (event === 'INSERT' || event === 'UPDATE')) {
      try { onOptimisticPatch(tableName, event, newRow) }
      catch (err) { console.warn(`[/tickets/${pageName}] optimistic patch error`, err) }
    }
    _debounceRefresh()
  }

  Promise.resolve(getSDK().getService?.('workspaceProject'))
    .then((wp) => {
      if (_cancelled || !wp?.realtime?.subscribeTickets) return
      _resolved = wp.realtime.subscribeTickets({ tables }, _onEvent)
      console.log(`[/tickets/${pageName}] realtime subscribed (${tables.join(', ')})`)
    })
    .catch((err) => {
      console.warn(`[/tickets/${pageName}] subscribe failed:`, err?.message || err)
    })

  return () => {
    _cancelled = true
    if (_debounceTimer) { clearTimeout(_debounceTimer); _debounceTimer = null }
    if (typeof _resolved === 'function') {
      try { _resolved() } catch (_) {}
    }
  }
}
